import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Plus, Pencil, Ban, Building2 } from "lucide-react";
import {
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
  useDeactivateCompany,
} from "../hooks/useAccounting";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import type { CompanyDto, CreateCompanyDto, UpdateCompanyDto } from "../services/accountingApi";

// ── Form state type ───────────────────────────────────────────────────────────

type FormState = {
  id: string;
  companyName: string;
  abbreviation: string;
  registrationNumber: string;
  taxId: string;
  phone: string;
  phone2: string;
  email: string;
  website: string;
  address: string;
  city: string;
  country: string;
};

const emptyForm = (): FormState => ({
  id: "",
  companyName: "",
  abbreviation: "",
  registrationNumber: "",
  taxId: "",
  phone: "",
  phone2: "",
  email: "",
  website: "",
  address: "",
  city: "",
  country: "Kenya",
});

// ── Company Form Dialog ───────────────────────────────────────────────────────

function CompanyDialog({
  open,
  onOpenChange,
  editMode,
  form,
  setForm,
  onSubmit,
  isPending,
  fieldErrors,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editMode: boolean;
  form: FormState;
  setForm: (f: FormState) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  fieldErrors: Record<string, string[]>;
}) {
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editMode ? "Edit Company" : "New Company"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* ── Identity ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field className="col-span-2">
              <FieldLabel>Company Name <span className="text-red-500">*</span></FieldLabel>
              <Input value={form.companyName} onChange={set("companyName")} required placeholder="e.g. K L Ventures Ltd" />
              {fieldErrors.companyName && <p className="text-red-500 text-xs mt-1">{fieldErrors.companyName.join(", ")}</p>}
            </Field>
            <Field>
              <FieldLabel>Abbreviation</FieldLabel>
              <Input value={form.abbreviation} onChange={set("abbreviation")} placeholder="e.g. ASL" maxLength={10} />
            </Field>
            <Field>
              <FieldLabel>Reg. Number</FieldLabel>
              <Input value={form.registrationNumber} onChange={set("registrationNumber")} placeholder="e.g. PVT-123456" />
            </Field>
            <Field>
              <FieldLabel>Tax ID / PIN</FieldLabel>
              <Input value={form.taxId} onChange={set("taxId")} placeholder="e.g. P051234567X" />
            </Field>
            <Field>
              <FieldLabel>Phone</FieldLabel>
              <Input value={form.phone} onChange={set("phone")} placeholder="+254 700 000 000" />
            </Field>
            <Field>
              <FieldLabel>Alt. Phone</FieldLabel>
              <Input value={form.phone2} onChange={set("phone2")} placeholder="+254 798 000 000" />
            </Field>
          </div>

          {/* ── Contact ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Email</FieldLabel>
              <Input type="email" value={form.email} onChange={set("email")} placeholder="info@company.com" />
              {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email.join(", ")}</p>}
            </Field>
            <Field>
              <FieldLabel>Website</FieldLabel>
              <Input value={form.website} onChange={set("website")} placeholder="https://company.com" />
            </Field>
          </div>

          {/* ── Address ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field className="col-span-2">
              <FieldLabel>Address</FieldLabel>
              <Input value={form.address} onChange={set("address")} placeholder="P.O. Box / Street" />
            </Field>
            <Field>
              <FieldLabel>City / Town</FieldLabel>
              <Input value={form.city} onChange={set("city")} placeholder="Nairobi" />
            </Field>
            <Field>
              <FieldLabel>Country</FieldLabel>
              <Input value={form.country} onChange={set("country")} placeholder="Kenya" />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : editMode ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function CompaniesPage() {
  const { data: companies, isLoading } = useCompanies();
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const deactivateMutation = useDeactivateCompany();
  const notify = useNotification();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState<FormState>(emptyForm());

  const openCreate = () => { setForm(emptyForm()); setEditMode(false); setFieldErrors({}); setDialogOpen(true); };

  const openEdit = (c: CompanyDto) => {
    setForm({
      id: c.id,
      companyName: c.companyName,
      abbreviation: c.abbreviation || "",
      registrationNumber: c.registrationNumber || "",
      taxId: c.taxId || "",
      phone: c.phone || "",
      phone2: c.phone2 || "",
      email: c.email || "",
      website: c.website || "",
      address: c.address || "",
      city: c.city || "",
      country: c.country || "Kenya",
    });
    setEditMode(true);
    setFieldErrors({});
    setDialogOpen(true);
  };

  const handleDeactivate = (id: string) => {
    deactivateMutation.mutate(id, {
      onSuccess: () => notify.success("Company deactivated"),
      onError: (err) => notify.error(getErrorMessage(err)),
    });
  };

  const nullStr = (s: string) => s.trim() || null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (editMode) {
      const dto: UpdateCompanyDto = {
        id: form.id,
        companyName: form.companyName,
        abbreviation: nullStr(form.abbreviation),
        registrationNumber: nullStr(form.registrationNumber),
        taxId: nullStr(form.taxId),
        phone: nullStr(form.phone),
        phone2: nullStr(form.phone2),
        email: nullStr(form.email),
        website: nullStr(form.website),
        address: nullStr(form.address),
        city: nullStr(form.city),
        country: nullStr(form.country),
      };
      updateMutation.mutate({ id: form.id, dto }, {
        onSuccess: () => { notify.success("Company updated"); setDialogOpen(false); },
        onError: (err) => { const fe = getFieldErrors(err); if (fe) setFieldErrors(fe); notify.error(getErrorMessage(err)); },
      });
    } else {
      const dto: CreateCompanyDto = {
        companyName: form.companyName,
        abbreviation: nullStr(form.abbreviation),
        registrationNumber: nullStr(form.registrationNumber),
        taxId: nullStr(form.taxId),
        phone: nullStr(form.phone),
        phone2: nullStr(form.phone2),
        email: nullStr(form.email),
        website: nullStr(form.website),
        address: nullStr(form.address),
        city: nullStr(form.city),
        country: nullStr(form.country),
      };
      createMutation.mutate(dto, {
        onSuccess: () => { notify.success("Company created"); setDialogOpen(false); setForm(emptyForm()); },
        onError: (err) => { const fe = getFieldErrors(err); if (fe) setFieldErrors(fe); notify.error(getErrorMessage(err)); },
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Company Profile</h1>
          <p className="text-muted-foreground">Manage the company identity reused across setup, reporting, and finance.</p>
        </div>
        {!companies?.length && (
          <PermissionGate permissions={["companies.create"]}>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> New Company</Button>
          </PermissionGate>
        )}
      </div>

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : !companies?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No companies yet. Create one to link cost centres.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Abbr.</TableHead>
                  <TableHead>Reg. No.</TableHead>
                  <TableHead>Tax ID</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-center">Cost Centres</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.companyName}</TableCell>
                    <TableCell className="font-mono text-sm">{c.abbreviation || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.registrationNumber || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.taxId || "—"}</TableCell>
                    <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                    <TableCell className="text-sm">{c.email || "—"}</TableCell>
                    <TableCell className="text-center text-sm">{c.costCentreCount}</TableCell>
                    <TableCell>
                      <Badge variant={c.isActive ? "default" : "outline"}>
                        {c.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <PermissionGate permissions={["companies.update"]}>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permissions={["companies.deactivate"]}>
                          {c.isActive && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeactivate(c.id)}>
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CompanyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editMode={editMode}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
        fieldErrors={fieldErrors}
      />
    </div>
  );
}
